import React, { useState, useEffect } from 'react';
import { adminApi, payPeriodsApi, formatDateRange } from '../../lib/api';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Download,
  FileText,
  Calendar,
  Clock,
  CheckCircle2,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const AdminReports = () => {
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [periodStats, setPeriodStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchPayPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchPeriodStats();
    }
  }, [selectedPeriod]);

  const fetchPayPeriods = async () => {
    try {
      const response = await payPeriodsApi.getAll();
      setPayPeriods(response.data);
      const current = response.data.find(p => p.is_current);
      if (current) {
        setSelectedPeriod(current.start_date);
      }
    } catch (error) {
      toast.error('Failed to load pay periods');
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodStats = async () => {
    try {
      const response = await adminApi.getTimeEntries({ payPeriodStart: selectedPeriod });
      const entries = response.data;
      
      const totalHours = entries.reduce((sum, e) => sum + e.hours, 0);
      const approvedHours = entries.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.hours, 0);
      const pendingHours = entries.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.hours, 0);
      const uniqueUsers = new Set(entries.map(e => e.user_id)).size;
      
      setPeriodStats({
        totalEntries: entries.length,
        totalHours,
        approvedHours,
        pendingHours,
        uniqueContractors: uniqueUsers
      });
    } catch (error) {
      console.error('Failed to fetch period stats');
    }
  };

  const handleDownload = async () => {
    if (!selectedPeriod) {
      toast.error('Please select a reporting period');
      return;
    }

    setDownloading(true);
    try {
      const response = await adminApi.exportPayPeriod(selectedPeriod);
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timesheet_${selectedPeriod}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
    } catch (error) {
      const message = error.response?.status === 404 
        ? 'No entries found for this reporting period' 
        : 'Failed to download report';
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const selectedPeriodData = payPeriods.find(p => p.start_date === selectedPeriod);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-reports-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            Reports
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Download pay period data for payroll processing
          </p>
        </div>

        {/* Period Selection */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-brick-cyan" aria-hidden="true" />
              Select Pay Period
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger className="w-full md:w-80 border-slate-300" data-testid="report-period-select">
                  <SelectValue placeholder="Select a period" />
                </SelectTrigger>
                <SelectContent>
                  {payPeriods.map((period) => (
                    <SelectItem key={period.start_date} value={period.start_date}>
                      {formatDateRange(period.start_date, period.end_date)}
                      {period.is_current && ' (Current)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={handleDownload}
                disabled={!selectedPeriod || downloading}
                className="bg-brick-red hover:bg-[#c4151d] font-medium"
                data-testid="download-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" aria-hidden="true" />
                {downloading ? 'Downloading...' : 'Download CSV'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Period Stats */}
        {selectedPeriodData && periodStats && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              Period Summary: <span className="text-brick-cyan">{formatDateRange(selectedPeriodData.start_date, selectedPeriodData.end_date)}</span>
            </h2>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Entries</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="total-entries">
                        {periodStats.totalEntries}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-brick-cyan/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-brick-cyan" aria-hidden="true" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Hours</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="report-total-hours">
                        {periodStats.totalHours.toFixed(1)}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-slate-600" aria-hidden="true" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Approved</p>
                      <p className="text-3xl font-bold text-green-600 mt-1" data-testid="report-approved-hours">
                        {periodStats.approvedHours.toFixed(1)}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" aria-hidden="true" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contractors</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1" data-testid="report-contractors">
                        {periodStats.uniqueContractors}
                      </p>
                    </div>
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-slate-600" aria-hidden="true" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* CSV Preview Info */}
            <Card className="bg-slate-50 border border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">CSV Export Contents</h3>
                <p className="text-slate-500 text-sm mb-4">
                  The exported CSV file will contain the following columns:
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Employee Name', 'Email', 'Date', 'Hours', 'Hourly Rate', 'Amount Owed', 'Project', 'Description', 'Status', 'Pay Period Start', 'Pay Period End'].map((col) => (
                    <span key={col} className="font-mono text-xs px-3 py-1.5 bg-white border border-slate-200 rounded-md text-brick-cyan">
                      {col}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminReports;
